import { ChevronRight, type LucideIcon } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'

export interface NavItem {
  title: string
  url: string
  icon?: LucideIcon
  isActive?: boolean
  /** When true, the children stay expanded and the user can't collapse them. */
  alwaysOpen?: boolean
  items?: {
    title: string
    url: string
  }[]
}

interface NavMainProps {
  items: NavItem[]
  label?: string
}

export function NavMain({ items, label }: NavMainProps) {
  const location = useLocation()
  const { setOpenMobile, isMobile } = useSidebar()

  const isPathActive = (path: string) => {
    if (path === '/forms') {
      return location.pathname.startsWith('/forms')
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Close mobile sidebar when navigating
  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <SidebarGroup>
      { label && <SidebarGroupLabel>{ label }</SidebarGroupLabel> }
      <SidebarMenu>
        { items.map((item) => {
          const hasChildren = item.items && item.items.length > 0
          const isActive = isPathActive(item.url) ||
            (item.items?.some(child => isPathActive(child.url)) ?? false)

          // Items with children get collapsible behavior
          if (hasChildren) {
            const alwaysOpen = item.alwaysOpen === true
            const collapsibleProps = alwaysOpen
              ? { open: true as const }
              : { defaultOpen: isActive }
            return (
              <Collapsible
                key={ item.title }
                asChild
                { ...collapsibleProps }
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={ item.title }
                      isActive={ isActive }
                      className={ alwaysOpen ? 'cursor-default' : undefined }
                    >
                      { item.icon && <item.icon /> }
                      <span>{ item.title }</span>
                      { !alwaysOpen && (
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      ) }
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      { item.items?.map((subItem) => (
                        <SidebarMenuSubItem key={ subItem.title }>
                          <SidebarMenuSubButton
                            asChild
                            isActive={ isPathActive(subItem.url) }
                          >
                            <NavLink to={ subItem.url } onClick={ handleNavClick }>
                              <span>{ subItem.title }</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )) }
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          }

          // Simple items without children
          return (
            <SidebarMenuItem key={ item.title }>
              <SidebarMenuButton
                asChild
                tooltip={ item.title }
                isActive={ isActive }
              >
                <NavLink to={ item.url } onClick={ handleNavClick }>
                  { item.icon && <item.icon /> }
                  <span>{ item.title }</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        }) }
      </SidebarMenu>
    </SidebarGroup>
  )
}
